import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ClipboardCheck, Loader2, Save, RotateCcw } from 'lucide-react';
import api from '../../utils/api';

// Inspection form data that prints as page 2 of the QC report.
// Empty = not entered (prints as a blank line) — nothing is defaulted to "pass".
const RESULT_OPTIONS = [
  { value: 'pass', label: 'Pass', on: 'bg-green-600 text-white border-green-600' },
  { value: 'fail', label: 'Fail', on: 'bg-red-600 text-white border-red-600' },
  { value: 'na',   label: 'N/A',  on: 'bg-gray-500 text-white border-gray-500' },
];
const FINAL_RESULTS = [
  { value: 'approved', label: 'Approved' },
  { value: 'rework', label: 'Rework Required' },
  { value: 'rejected', label: 'Rejected' },
];
const QTY_FIELDS = [
  ['order_qty', 'Order Qty'], ['inspected_qty', 'Inspected Qty'],
  ['accepted_qty', 'Accepted'], ['rework_qty', 'Rework'], ['rejected_qty', 'Rejected'],
];

function toDateInput(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
const num = v => (v == null ? '' : String(+Number(v).toFixed(2)));

function fromReport(r) {
  return {
    mo_number: r.mo_number || '', drawing_revision: r.drawing_revision || '', finish_code: r.finish_code || '',
    order_qty: num(r.order_qty), inspected_qty: num(r.inspected_qty), accepted_qty: num(r.accepted_qty),
    rework_qty: num(r.rework_qty), rejected_qty: num(r.rejected_qty),
    checklist: (r.checklist || []).map(c => ({ key: c.key, label: c.label, criteria: c.criteria, result: c.result || null, remark: c.remark || '' })),
    inspection_result: r.inspection_result || '', qc_remarks: r.qc_remarks || '',
    production_head: r.production_head || '', signoff_date: toDateInput(r.signoff_date),
  };
}

export default function QcInspectionForm({ report, editable, onSaved, onDirtyChange }) {
  const initial = useMemo(() => fromReport(report), [report]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial); }, [initial]);

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setRemark = (key, value) => setForm(f => ({
    ...f, checklist: f.checklist.map(c => (c.key === key ? { ...c, remark: value } : c)),
  }));
  const setCheck = (key, value) => setForm(f => ({
    ...f, checklist: f.checklist.map(c => (c.key === key ? { ...c, result: c.result === value ? null : value } : c)),
  }));

  // Values the print will use when the field is left empty (read from linked records)
  const auto = {
    mo_number: report.main_po_no,
    finish_code: (report.finish_sheet_codes || []).join(', ') || report.main_po_finishes_note,
    order_qty: report.item_quantity != null ? `${num(report.item_quantity)}${report.item_unit ? ' ' + report.item_unit : ''}` : null,
  };
  const outcome = ['accepted_qty', 'rework_qty', 'rejected_qty'].reduce((s, k) => s + (Number(form[k]) || 0), 0);
  const overInspected = form.inspected_qty !== '' && outcome > Number(form.inspected_qty);
  const counts = form.checklist.reduce((a, c) => { a[c.result || 'open'] += 1; return a; }, { pass: 0, fail: 0, na: 0, open: 0 });

  const save = async () => {
    if (overInspected) return toast.error('Accepted + Rework + Rejected cannot exceed Inspected Qty');
    setSaving(true);
    try {
      await api.put(`/quality/reports/${report.id}/inspection`, {
        ...form, checklist: form.checklist.map(c => ({ key: c.key, result: c.result, remark: c.remark })),
      });
      toast.success('Inspection form saved');
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const input = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm disabled:bg-gray-50 disabled:text-gray-500';

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-gray-500" />
          <span className="font-semibold text-gray-800">Inspection Form</span>
          <span className="text-xs text-gray-400">— prints as page 2 of the report</span>
        </div>
        {editable && (
          <div className="flex items-center gap-2">
            {dirty && (
              <button onClick={() => setForm(initial)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-white">
                <RotateCcw className="w-3.5 h-3.5" /> Discard
              </button>
            )}
            <button onClick={save} disabled={saving || !dirty}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold disabled:opacity-40">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
          </div>
        )}
      </div>

      <div className="p-4 space-y-5">
        {/* Order & product */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="text-xs font-semibold text-gray-500">MO No.
            <input className={input} disabled={!editable} value={form.mo_number} onChange={e => set('mo_number', e.target.value)}
              placeholder={auto.mo_number ? `Auto: ${auto.mo_number}` : ''} />
          </label>
          <label className="text-xs font-semibold text-gray-500">Drawing / Revision
            <input className={input} disabled={!editable} value={form.drawing_revision} onChange={e => set('drawing_revision', e.target.value)} />
          </label>
          <label className="text-xs font-semibold text-gray-500">Finish / Fabric Code
            <input className={input} disabled={!editable} value={form.finish_code} onChange={e => set('finish_code', e.target.value)}
              placeholder={auto.finish_code ? `Auto: ${auto.finish_code}` : ''} />
          </label>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {QTY_FIELDS.map(([k, label]) => (
            <label key={k} className="text-xs font-semibold text-gray-500">{label}
              <input type="number" min="0" step="any" className={input} disabled={!editable} value={form[k]}
                onChange={e => set(k, e.target.value)}
                placeholder={k === 'order_qty' && auto.order_qty ? `Auto: ${auto.order_qty}` : ''} />
            </label>
          ))}
        </div>
        {overInspected && <p className="text-xs text-red-600 -mt-3">Accepted + Rework + Rejected ({outcome}) is more than Inspected Qty.</p>}

        {/* Checklist */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h4 className="text-sm font-semibold text-gray-700">Quality Inspection Checklist</h4>
            <div className="flex gap-3 text-xs text-gray-500">
              <span>Pass <b className="text-green-700">{counts.pass}</b></span>
              <span>Fail <b className="text-red-700">{counts.fail}</b></span>
              <span>N/A <b className="text-gray-700">{counts.na}</b></span>
              {counts.open > 0 && <span>Not inspected <b className="text-amber-700">{counts.open}</b></span>}
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {form.checklist.map(c => (
              <div key={c.key} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <div className="min-w-0 flex-1 basis-48">
                  <p className="text-sm font-medium text-gray-800">{c.label}</p>
                  <p className="text-xs text-gray-400">{c.criteria}</p>
                </div>
                <input value={c.remark} onChange={e => setRemark(c.key, e.target.value)} disabled={!editable} maxLength={200}
                  placeholder={editable ? 'Remark (optional)' : ''}
                  className="w-full sm:w-44 order-last sm:order-none px-2 py-1 border border-gray-200 rounded-md text-xs disabled:bg-gray-50 disabled:text-gray-500" />
                <div className="flex gap-1" role="radiogroup" aria-label={c.label}>
                  {RESULT_OPTIONS.map(o => (
                    <button key={o.value} type="button" role="radio" aria-checked={c.result === o.value} disabled={!editable}
                      onClick={() => setCheck(c.key, o.value)}
                      className={`px-2.5 py-1 rounded-md border text-xs font-semibold transition-colors disabled:cursor-not-allowed ${
                        c.result === o.value ? o.on : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {editable && <p className="text-[11px] text-gray-400 mt-1">Click a selected result again to clear it. Unmarked points print as empty boxes.</p>}
        </div>

        {/* Final disposition */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="text-xs font-semibold text-gray-500">Inspection Result
            <select className={input} disabled={!editable} value={form.inspection_result} onChange={e => set('inspection_result', e.target.value)}>
              <option value="">— Not decided —</option>
              {FINAL_RESULTS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
          <label className="text-xs font-semibold text-gray-500">Production Head
            <input className={input} disabled={!editable} value={form.production_head} onChange={e => set('production_head', e.target.value)} />
          </label>
          <label className="text-xs font-semibold text-gray-500">Sign-off Date
            <input type="date" className={input} disabled={!editable} value={form.signoff_date} onChange={e => set('signoff_date', e.target.value)} />
          </label>
        </div>
        <label className="block text-xs font-semibold text-gray-500">QC Remarks
          <textarea rows={3} className={input} disabled={!editable} value={form.qc_remarks} onChange={e => set('qc_remarks', e.target.value)} />
        </label>
        <p className="text-[11px] text-gray-400">QC Inspector on the printed form is the report's QC Person{report.qc_person_name ? ` (${report.qc_person_name})` : ' — not set yet'}.</p>
      </div>
    </div>
  );
}
